import React, { useCallback } from 'react';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n/useTranslation';
import type { SurveyStepProps } from '../types';
import { StepHeader } from '../components/StepHeader';
import { NavigationButton } from '../../components/NavigationButton';
import { STEP_MINIMUM_HEIGHT } from '../consts';
import { useLogoutEventSessionMutation } from '@graphql/generated';
import { EMPTY_CONTACT_VALUES } from '../../hooks/useEventFormState';

export const ConfirmGuestStep = (props: SurveyStepProps) => {
  const { hasNext, onNext, guestInfo, setFormState } = props;
  const { tq } = useTranslation('eventFormDisplay');
  const [logoutEventSession] = useLogoutEventSessionMutation();

  const guestName = guestInfo?.guestDisplayName; // This component will never be shown if guestInfo is undefined.
  const onNotMe = useCallback(async () => {
    try {
      await logoutEventSession({
        refetchQueries: () => [
          'GetEventSession',
          'GetGuestSiteSchedule',
          'GetEventCTAButtonsData',
          'EventFormFunctionalWrapperActiveSession',
          'EventFormFunctionalWrapperGuestAnswers',
        ],
      });
    } catch {
      // We proceed even if the logout request fails so the guest can still continue.
    } finally {
      setFormState({
        formValues: {},
        attendanceValue: null,
        contactValues: { ...EMPTY_CONTACT_VALUES },
      });
      if (hasNext) {
        onNext();
      }
    }
  }, [hasNext, logoutEventSession, onNext, setFormState]);

  // Determine translations based on submission status
  const translations = (() => {
    switch (guestInfo?.hasSubmitted) {
      case true:
        return {
          heading: tq`Welcome Back`,
          subheading: tq`You've already responded to this form`,
          bodyText: tq`Hi, ${guestName}, would you like to edit your response?`,
          primaryButtonText: tq`Edit Response`,
          secondaryButtonText: tq`Not You?`,
        };
      case false:
      default:
        return {
          heading: tq`Welcome`,
          subheading: tq`Let's make sure we have the right person`,
          bodyText: tq`Would you like to respond to this form as ${guestName}?`,
          primaryButtonText: tq`Continue`,
          secondaryButtonText: tq`Not You?`,
        };
    }
  })();

  return (
    <Box>
      <SpacingStack gap="1rem">
        <Box minHeight={STEP_MINIMUM_HEIGHT}>
          <StepHeader
            heading={translations.heading}
            subheading={translations.subheading}
          />
          <TextV2 typographyVariant="body1">{translations.bodyText}</TextV2>
        </Box>
        <Flex
          marginTop="32px"
          width="100%"
          columnGap="6"
        >
          <NavigationButton
            onClick={() => {
              void onNotMe();
            }}
            direction="prev"
            text={translations.secondaryButtonText}
          />
          {hasNext && (
            <NavigationButton
              onClick={onNext}
              direction="next"
              text={translations.primaryButtonText}
            />
          )}
        </Flex>
      </SpacingStack>
    </Box>
  );
};
